import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { useDataContext } from "../hub/data-context-provider.js";
/**
 * Right-hand sidebar that queues pending decisions in creation order.
 * One card per decision; each card walks every question with single-
 * select choices + an implicit "Other" free-text option. Submitting
 * the full set of answers calls `answerDecision` and removes the card
 * from the queue (the relay broadcast that follows will refresh siblings).
 */
export function DecisionsSidebar({ workspaceId, refetchKey }) {
    const ctx = useDataContext();
    const [decisions, setDecisions] = useState(null);
    const [error, setError] = useState(null);
    useEffect(() => {
        let cancelled = false;
        setError(null);
        ctx.hub
            .listDecisions(workspaceId, "pending")
            .then((list) => {
            if (cancelled)
                return;
            const sorted = [...list].sort((a, b) => a.created_at.localeCompare(b.created_at));
            setDecisions(sorted);
        })
            .catch((e) => {
            if (!cancelled)
                setError(e instanceof Error ? e.message : String(e));
        });
        return () => {
            cancelled = true;
        };
    }, [ctx.hub, workspaceId, refetchKey]);
    const handleAnswered = (answeredId) => {
        setDecisions((prev) => prev?.filter((d) => d.id !== answeredId) ?? null);
    };
    return (_jsxs("aside", { style: containerStyle, children: [_jsxs("div", { style: headerStyle, children: [_jsx("span", { style: headerTitleStyle, children: "Decisions" }), decisions && (_jsx("span", { style: countBadgeStyle, children: decisions.length }))] }), error && _jsx("div", { style: errorStyle, children: error }), decisions === null ? (_jsx("div", { style: emptyStyle, children: "Loading\u2026" })) : decisions.length === 0 ? (_jsx("div", { style: emptyStyle, children: "No decisions waiting. When an agent asks for a call, it'll show up here." })) : (_jsx("div", { style: listStyle, children: decisions.map((d) => (_jsx(DecisionCard, { decision: d, onAnswered: handleAnswered }, d.id))) }))] }));
}
function DecisionCard({ decision, onAnswered, }) {
    const ctx = useDataContext();
    const [draft, setDraft] = useState(new Map());
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const allAnswered = decision.questions.every((q) => {
        const a = draft.get(q.id);
        if (!a)
            return false;
        if (a.choice_id)
            return true;
        return typeof a.other_text === "string" && a.other_text.trim().length > 0;
    });
    const setAnswer = (questionId, answer) => {
        setDraft((prev) => {
            const next = new Map(prev);
            next.set(questionId, answer);
            return next;
        });
    };
    const submit = async () => {
        setSubmitting(true);
        setError(null);
        try {
            const answers = decision.questions
                .map((q) => draft.get(q.id))
                .filter((a) => Boolean(a));
            await ctx.hub.answerDecision(decision.id, answers);
            onAnswered(decision.id);
        }
        catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        }
        finally {
            setSubmitting(false);
        }
    };
    return (_jsxs("div", { style: cardStyle, children: [decision.questions.map((q) => (_jsx(QuestionForm, { question: q, answer: draft.get(q.id), onChange: (a) => setAnswer(q.id, a) }, q.id))), error && _jsx("div", { style: errorStyle, children: error }), _jsx("button", { type: "button", onClick: () => void submit(), disabled: !allAnswered || submitting, style: {
                    ...submitBtnStyle,
                    background: !allAnswered || submitting ? "var(--bg-hover)" : "var(--accent)",
                    color: "white",
                    cursor: !allAnswered || submitting ? "not-allowed" : "pointer",
                }, children: submitting ? "Submitting…" : "Submit" })] }));
}
function QuestionForm({ question, answer, onChange, }) {
    const isOther = !!answer?.other_text || (!answer?.choice_id && answer?.other_text === "");
    return (_jsxs("div", { style: questionStyle, children: [_jsx("div", { style: promptStyle, children: question.prompt }), _jsxs("div", { style: choicesStyle, children: [question.choices.map((c) => (_jsxs("label", { style: choiceLabelStyle, children: [_jsx("input", { type: "radio", name: `q-${question.id}`, checked: answer?.choice_id === c.id, onChange: () => onChange({ question_id: question.id, choice_id: c.id }) }), _jsx("span", { children: c.label })] }, c.id))), _jsxs("label", { style: choiceLabelStyle, children: [_jsx("input", { type: "radio", name: `q-${question.id}`, checked: isOther, onChange: () => onChange({ question_id: question.id, other_text: "" }) }), _jsx("span", { children: "Other" })] }), isOther && (_jsx("input", { type: "text", value: answer?.other_text ?? "", onChange: (e) => onChange({ question_id: question.id, other_text: e.target.value }), placeholder: "Type your answer\u2026", style: otherInputStyle, autoFocus: true }))] })] }));
}
const containerStyle = {
    width: 320,
    background: "var(--bg-secondary)",
    borderLeft: "1px solid var(--border)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
};
const headerStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 16px",
    borderBottom: "1px solid var(--border)",
    flexShrink: 0,
};
const headerTitleStyle = {
    fontSize: 13,
    fontWeight: 600,
    color: "var(--text-primary)",
    textTransform: "uppercase",
    letterSpacing: 0.5,
};
const countBadgeStyle = {
    background: "var(--accent)",
    color: "white",
    fontSize: 11,
    fontWeight: 600,
    padding: "2px 8px",
    borderRadius: 10,
    minWidth: 18,
    textAlign: "center",
};
const listStyle = {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    padding: 12,
    overflowY: "auto",
};
const cardStyle = {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    padding: 14,
    background: "var(--bg-inset, #0d0e11)",
    border: "1px solid var(--border)",
    borderRadius: 8,
};
const questionStyle = {
    display: "flex",
    flexDirection: "column",
    gap: 6,
};
const promptStyle = {
    fontSize: 13,
    color: "var(--text-primary)",
    lineHeight: 1.4,
};
const choicesStyle = {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    paddingLeft: 4,
};
const choiceLabelStyle = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 12,
    color: "var(--text-secondary)",
    cursor: "pointer",
};
const otherInputStyle = {
    marginTop: 4,
    marginLeft: 24,
    padding: "6px 10px",
    background: "var(--bg-tertiary)",
    border: "1px solid var(--border)",
    borderRadius: 4,
    color: "var(--text-primary)",
    fontSize: 12,
    fontFamily: "inherit",
    outline: "none",
};
const submitBtnStyle = {
    padding: "8px 14px",
    fontSize: 13,
    fontWeight: 500,
    border: "none",
    borderRadius: 6,
    fontFamily: "inherit",
};
const emptyStyle = {
    padding: 24,
    fontSize: 12,
    color: "var(--text-muted)",
    textAlign: "center",
};
const errorStyle = {
    margin: "8px 12px",
    padding: "8px 10px",
    borderRadius: 4,
    background: "rgba(250, 82, 82, 0.1)",
    border: "1px solid var(--error)",
    color: "var(--error)",
    fontSize: 11,
};
//# sourceMappingURL=DecisionsSidebar.js.map